import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnInit,
    Output,
    Input,
} from "@angular/core";
import {
    FormArray,
    FormBuilder,
    FormGroup,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from "@angular/forms";
import { AccountService } from "../../../data-access-layer/account/account.service";
import { CreateAccountRequest } from "../../../model/account/account";
import { NotifService } from "../../../core/notification/notif.service";
import { UserStateService } from "../../../auth/user-state-service";
import { AuthService } from "../../../auth/auth.service";
import { OverlaysService } from "src/app/data-access-layer/global-overlays/overlays.service";
import { ActivatedRoute } from "@angular/router";
import { CustomScoringService } from "../services/custom-scoring.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { GroupService } from "src/app/data-access-layer/groups/group.service";
import { DatapointsAggregateService } from "src/app/data-access-layer/datapoints/datapoints-aggregate.service";
import { WorkspaceItemService } from "src/app/data-access-layer/workspace-item/workspace-item.service";
import { WorkspaceItemType } from "src/app/model/workspace/workspace-item-type";
import { WorkspaceItemAccess } from "src/app/model/workspace/workspace-item-access";
import { CustomScoringScope } from "../model/custom-service";
import { catchError } from 'rxjs/operators';
import { of, range } from 'rxjs';
import { CustomErrorStateMatcher } from "src/app/shared/error-state-matcher";
import { decodeHtmlText } from "src/app/core/utils/util-master";

@Component({
    selector: "add-custom-scoring",
    templateUrl: "./add-custom-scoring.component.html",
    styleUrls: ["./add-custom-scoring.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddCustomScoringComponent implements OnInit {
    @Output() updatedSuccessfully = new EventEmitter();
    @Output() onCancelClose = new EventEmitter();
    @Input() existingData: any; 
    @Input() fromGlobal: any;
    @Input() globalDataSetRecord: any;
    @Input() externalOverlaysFromGlobal: any;
    @Input() locationDatasetRecord: any;
    @Input() dataToEdit: any;
    @Input() isEdit: boolean;
    @Input() dropDownValueTextOptions: any;
    @Input() customScoringScope: any;
    createAccountForm: UntypedFormGroup;
    datasetLinkMap = {};
    externalOverlays: any;
    accountId: number;
    selectedType: string = '';
    isNumberField = false;
    isTextField = false;
    isDropdownValueSelected = false;
    textOptions = [];
    minMaxValues : any;
    locationDataset: any;
    isFormDisabled = false;
    minValues: any;
    isApplied: boolean = false;
    fieldHasDecimals: boolean = false;
    fieldGroups: any[] = [];
    collapsedGroups: { [key: string]: boolean } = {};
    collapsedSubGroups: { [key: string]: boolean } = {};
    climateOverlays: any[] = [];
    climateGroupNames = ['Climate Change', 'Climate Change Risk Score'];
    selectedGroupDatasetID: string = '';
    uniqueGroupsCache: any[] = [];
    overlaysByGroupCache: { [key: string]: any[] } = {};
    formulas: any[] = [];
    isDropdownInitialized: boolean = false;
    originalFieldValue: any = null;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly accountService: AccountService,
        private readonly notifService: NotifService,
        private readonly changeDetector: ChangeDetectorRef,
        private readonly userStateService: UserStateService,
        private readonly auth: AuthService,
        private overlayService: OverlaysService,
        private readonly route: ActivatedRoute,
        private customService: CustomScoringService,
        private fb: FormBuilder,
        private readonly groupService: GroupService,
        private readonly datapointsAggregateService: DatapointsAggregateService,
        private readonly workspaceItemService: WorkspaceItemService,

    ) {
    }
    ngOnChanges() {
        if (!this.isEdit) {
            this.initForm();
            this.createAccountForm.controls.name.setValue('');
            this.createAccountForm.controls.fieldTextAndNumeric.setValue('');
            this.createAccountForm.controls.ranges.setValue([]);
            // Clear caches so template re-reads from fieldGroups
            this.uniqueGroupsCache = [];
            this.overlaysByGroupCache = {};
        }
        if (this.externalOverlaysFromGlobal) {
            this.externalOverlays = this.externalOverlaysFromGlobal;
            this.locationDataset = this.locationDatasetRecord;
        }
        if (this.globalDataSetRecord) {
            this.accountId = +this.route.snapshot.paramMap.get("accountId");
            if (!this.isEdit) {
                this.initForm();
                if ((this.customScoringScope === CustomScoringScope.EXTERNAL || this.customScoringScope === CustomScoringScope.GLOBAL)) {
                    this.isDropdownValueSelected = false;
                }
                this.getData();
            }
        }
    }

    /**
     * Public method called by parent component after all edit data is ready.
     * This ensures fieldGroups, existingData, and scope are all set before patching.
     */
    initEditMode(): void {
        // Clear all caches and state to prevent data leakage between different custom scorings
        this.uniqueGroupsCache = [];
        this.overlaysByGroupCache = {};
        this.collapsedGroups = {};
        this.collapsedSubGroups = {};
        this.isDropdownInitialized = false;
        this.originalFieldValue = null;
        this.isDropdownValueSelected = false;
        this.isNumberField = false;
        this.isTextField = false;
        this.textOptions = [];
        this.minMaxValues = null;
        this.minValues = null;
        this.fieldHasDecimals = false;
        
        // Pre-expand the group that contains the selected field based on scope
        if (this.existingData) {
            if (this.existingData.scope === CustomScoringScope.FORMULA || this.existingData.scope === 'FORMULA') {
                this.collapsedGroups['Formulas'] = false;
                this.collapsedGroups['Locations'] = true;
            } else if (this.existingData.scope === CustomScoringScope.INTERNAL || this.existingData.scope === 'INTERNAL' || this.existingData.scope === 'Locations') {
                this.collapsedGroups['Locations'] = false;
                this.collapsedGroups['Formulas'] = true;
            } else if (this.existingData.scope === CustomScoringScope.GLOBAL || this.existingData.scope === 'GLOBAL') {
                this.collapsedGroups['Climate'] = false;
            }
            // Mark dropdown as initialized since we've set the collapsed state
            this.isDropdownInitialized = true;
        }
        
        this.initForm();
        this.accountId = +this.route.snapshot.paramMap.get("accountId");
        this.patchExistingData();
        this.changeDetector.detectChanges();
    }

    ngOnInit() {
        this.accountId = +this.route.snapshot.paramMap.get("accountId");
        if (!this.isEdit) {
            this.initForm();
            this.createAccountForm.reset();
            this.createAccountForm.controls.name.setValue('');
            this.createAccountForm.controls.fieldTextAndNumeric.setValue('');
            this.createAccountForm.controls.ranges.setValue([]);
            this.getData();
        }
    }

    validateInput(event: any): void {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        
        // Allow only numbers, decimal point, commas, and minus sign
        // This runs during typing to prevent unwanted characters
        const validPattern = /^-?[\d,]*\.?\d*$/;
        
        if (value && !validPattern.test(value)) {
            // Remove invalid characters but keep the valid ones
            input.value = value.replace(/[^0-9.,\-]/g, '');
        }
    }

    formatNumber(event: any, rangeIndex?: number): void {
        const input = event.target as HTMLInputElement;
        let value = input.value;
        
        // Handle negative sign
        const isNegative = value.startsWith('-');
        
        // Remove everything except digits, decimal point, and commas
        let cleanValue = value.replace(/[^0-9.,]/g, '');
        
        // Remove commas (they'll be re-added during formatting)
        cleanValue = cleanValue.replace(/,/g, '');
        
        // Handle multiple decimal points - keep only the first one
        const decimalCount = (cleanValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
            const parts = cleanValue.split('.');
            cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        // If value is empty or just '-', leave as is
        if (cleanValue === '' || value === '-') {
            return;
        }
        
        // Parse the number
        const numValue = parseFloat(cleanValue);
        
        // Validate: check for NaN, Infinity, and safe integer range
        if (isNaN(numValue)) {
            return;
        }
        
        if (!isFinite(numValue)) {
            this.notifService.error('Invalid number: Infinity is not allowed');
            input.value = '';
            return;
        }
        
        if (Math.abs(numValue) > Number.MAX_SAFE_INTEGER) {
            this.notifService.error('Number is too large. Maximum safe value is ' + Number.MAX_SAFE_INTEGER.toLocaleString());
            return;
        }
        
        let formatted: string;
        
        // Allow decimals for all numeric fields
        const decimalParts = cleanValue.split('.');
        
        if (decimalParts.length > 1 && decimalParts[1].length > 2) {
            // More than 2 decimals - round to 2
            const rounded = Math.round(numValue * 100) / 100;
            // Convert to string without forcing .00
            const roundedStr = rounded.toString();
            const parts = roundedStr.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            formatted = parts.join('.');
        } else {
            // 0, 1, or 2 decimals - keep as is with comma formatting
            const parts = cleanValue.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            formatted = parts.join('.');
        }
        
        // Apply negative sign if needed
        if (isNegative) {
            formatted = '-' + formatted;
        }
        
        input.value = formatted;
        
        // Update the form control value if rangeIndex is provided
        if (rangeIndex !== undefined && rangeIndex !== null) {
            const control = this.ranges.at(rangeIndex);
            const fieldName = input.getAttribute('formControlName');
            if (fieldName && control) {
                control.get(fieldName)?.setValue(formatted, { emitEvent: false });
            }
        }
    }

    formatWithCommas(value: string, isComingFromUpdate = false): string {
        const cleanValue = value.toString().replace(/,/g, '');
        const num = parseFloat(cleanValue);
        
        if (isNaN(num)) {
            return value;
        }
        
        // Check for Infinity and unsafe numbers
        if (!isFinite(num) || Math.abs(num) > Number.MAX_SAFE_INTEGER) {
            return value; // Return original value if invalid
        }
        
        let formatted: string;
        
        if (!this.fieldHasDecimals) {
            // For integer fields, round to whole number
            const rounded = Math.round(num);
            formatted = rounded.toString();
            // Add commas
            formatted = formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else {
            // For decimal fields, limit to 2 decimals only if more exist
            const decimalParts = cleanValue.split('.');
            
            if (decimalParts.length > 1 && decimalParts[1].length > 2) {
                // More than 2 decimals - round to 2
                const rounded = Math.round(num * 100) / 100;
                formatted = rounded.toString();
            } else {
                // Keep as is
                formatted = num.toString();
            }
            
            // Add commas
            const parts = formatted.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            formatted = parts.join('.');
        }
        
        return formatted;
    }
    
    formatToTwoDecimals(value: any): string {
        if (value === null || value === undefined || value === '') {
            return '';
        }
        
        const cleanValue = value.toString().replace(/,/g, '');
        const num = parseFloat(cleanValue);
        
        if (isNaN(num)) {
            return '';
        }
        
        // Check for Infinity and unsafe numbers
        if (!isFinite(num) || Math.abs(num) > Number.MAX_SAFE_INTEGER) {
            return ''; // Return empty if invalid
        }
        
        // If field doesn't have decimals, return as integer
        if (!this.fieldHasDecimals) {
            return Math.round(num).toString();
        }
        
        // For decimal fields, limit to 2 decimals only if more exist
        const decimalParts = cleanValue.split('.');
        
        if (decimalParts.length > 1 && decimalParts[1].length > 2) {
            // More than 2 decimals - round to 2
            const rounded = Math.round(num * 100) / 100;
            return rounded.toString();
        }
        
        // Keep as is (0, 1, or 2 decimals)
        return num.toString();
    }
    
    checkIfFieldHasDecimals(minValue: any, maxValue: any): boolean {
        // Check if min or max values have decimal parts
        if (minValue !== null && minValue !== undefined) {
            const minNum = parseFloat(minValue.toString());
            if (!isNaN(minNum) && minNum % 1 !== 0) {
                return true;
            }
        }
        
        if (maxValue !== null && maxValue !== undefined) {
            const maxNum = parseFloat(maxValue.toString());
            if (!isNaN(maxNum) && maxNum % 1 !== 0) {
                return true;
            }
        }
        
        return false;
    }

    patchExistingData(): void {
        // Set customScoringScope from existingData if not already set correctly
        if (this.existingData.scope && this.customScoringScope !== this.existingData.scope) {
            this.customScoringScope = this.existingData.scope;
        }

        // Determine the field value based on scope
        let fieldValue = this.existingData.fieldName;
        
        if (this.existingData.scope === CustomScoringScope.GLOBAL && this.existingData.datasetID) {
            // For GLOBAL scope (climate fields), construct composite ID
            this.selectedGroupDatasetID = this.existingData.datasetID;
            fieldValue = `${this.existingData.datasetID}_${this.existingData.fieldId}`;
        } else if (this.existingData.scope === CustomScoringScope.FORMULA) {
            // For FORMULA scope, use field ID directly
            fieldValue = this.existingData.fieldId;
        } else if (this.existingData.scope === CustomScoringScope.INTERNAL || this.existingData.scope === 'Locations') {
            // For INTERNAL scope (location fields), use field ID
            fieldValue = this.existingData.fieldId;
        }

        this.createAccountForm.patchValue({
            name: this.existingData.name,
            fieldTextAndNumeric: fieldValue,
        });
        
        // Trigger field type change to fetch statistics and set up field properly
        this.onFieldTypeChange({
            value: fieldValue,
        }, true);
        
        if (this.existingData.scoring && this.existingData.scoring.length > 0) {
            this.isDropdownValueSelected = true;

            // Detect if existing data has decimal values
            this.fieldHasDecimals = this.existingData.scoring.some((range: any) => {
                return this.checkIfFieldHasDecimals(range.minNumberValue, range.maxNumberValue);
            });

            const rangesFormArray = this.createAccountForm.get('ranges') as FormArray;

            while (rangesFormArray.length !== 0) {
                rangesFormArray.removeAt(0);
            }

            this.existingData.scoring.forEach((range, index) => {
            // Check for Double.MAX_VALUE and Double.MIN_VALUE from backend
            // These represent open-ended ranges and should display as blank
            const isMinDefault = range.minNumberValue === null || 
                                 range.minNumberValue === -1.7976931348623157e+308 ||
                                 range.minNumberValue <= -1.7976931348623155e+308;
            const isMaxDefault = range.maxNumberValue === null || 
                                 range.maxNumberValue === 1.7976931348623157e+308 ||
                                 range.maxNumberValue >= 1.7976931348623155e+308;
            
            // Check if this min was pre-populated from previous range
            const isMinPrePopulated = index > 0 && !isMinDefault && 
                                      this.existingData.scoring[index - 1].maxNumberValue === range.minNumberValue;
            
            const minValueFormatted = isMinDefault ? '' : this.formatWithCommas(range.minNumberValue, true);
            
            const rangeGroup = this.fb.group({
                minValue: [minValueFormatted],
                maxValue: [isMaxDefault ? '' : this.formatWithCommas(range.maxNumberValue, true)],
                textValues: [range.textValues],
                label: [range.label, Validators.required],
                isMinPrePopulated: [isMinPrePopulated],
                originalPrePopulatedMin: [minValueFormatted]  // Store original for validation
            });

            rangesFormArray.push(rangeGroup);
            });

            this.changeDetector.detectChanges();
        }

        this.isApplied = this.existingData?.applied || false;
        this.isFormDisabled = this.isApplied;
        this.isApplied ? this.createAccountForm.disable() : this.createAccountForm.enable();
    }

    toggleApplyUnapply(): void {
        this.isApplied = !this.isApplied;
        this.isFormDisabled = this.isApplied;
    
        if (this.isApplied) {
            this.createAccountForm.disable();
            this.customService.applyFlag(this.accountId, this.existingData._id, true).subscribe(() => {
                this.notifService.success("Successfully applied.");
                this.refreshListing();
            });
        } else {
            this.createAccountForm.enable();
            this.customService.applyFlag(this.accountId,this.existingData._id, false).subscribe(() => {
                this.notifService.success("Successfully unapplied.");
                this.refreshListing();
            });
        }
        this.changeDetector.detectChanges();
    }

    refreshListing(): void {
        this.updatedSuccessfully.emit();
    }    

    getData() {
        // If externalOverlaysFromGlobal is provided (for climate editing), use it directly
        // But don't overwrite fieldGroups if it's already set by parent (for edit mode)
        if (this.externalOverlaysFromGlobal && this.customScoringScope === CustomScoringScope.GLOBAL) {
            this.externalOverlays = this.externalOverlaysFromGlobal;
            // Only set fieldGroups if not already set by parent (edit mode)
            if (!this.fieldGroups || this.fieldGroups.length === 0) {
                this.fieldGroups = [{
                    groupName: this.globalDataSetRecord?.name || 'Global Dataset',
                    fields: this.externalOverlaysFromGlobal,
                    datasetID: this.globalDataSetRecord?.id || ''
                }];
            }
            this.selectedGroupDatasetID = this.globalDataSetRecord?.id || '';
            // Call patchExistingData if existingData is present and fieldGroups is now ready
            if (this.existingData && this.fieldGroups && this.fieldGroups.length > 0) {
                this.patchExistingData();
            }
            return;
        }

        // Only fetch climate overlays for Locations/INTERNAL scope
        if (this.customScoringScope !== CustomScoringScope.INTERNAL && this.customScoringScope !== 'Locations') {
            return;
        }

        let locationDataSetFieldsItem = localStorage.getItem("locationCustomFields_"+this.accountId);
        if (locationDataSetFieldsItem === null || locationDataSetFieldsItem === undefined || locationDataSetFieldsItem === 'undefined') {
            this.accountService.findAccount(this.accountId).subscribe((account) => {
           const locationDataset = account.datasets.find((dataset: any) => dataset.name === 'Locations');
                 if (locationDataset) {
                    let locationDataSetItem = localStorage.getItem("locationDataSetItem_"+this.accountId);
                    if (locationDataSetItem === null || locationDataSetItem === undefined || locationDataSetItem === 'undefined') {
                        localStorage.setItem("locationDataSetItem_"+this.accountId, JSON.stringify(locationDataset));
                    }
                     this.locationDataset = JSON.parse(locationDataSetItem);

                     this.customService.getLocationCustomScoringFields(locationDataset?.id)
                     .subscribe((data: any) => {
                       let dropDownData = data.filter(field => field.isHighCardinality === false && field.baseType !== 'DATE_TIME');

                       localStorage.setItem("locationCustomFields_"+this.accountId, JSON.stringify(dropDownData))
                       if (this.externalOverlaysFromGlobal) {

                       } else {
                           // Create grouped structure for Locations
                           this.fieldGroups = [{
                               groupName: 'Locations',
                               fields: dropDownData
                           }];
                           this.externalOverlays = dropDownData;

                           // Fetch climate overlays for Locations scope
                           // CLIMATE DISABLED: Commented out to hide climate from custom scoring
                           // this.fetchClimateOverlays();

                           // Fetch formulas for Locations scope
                           this.fetchFormulas(locationDataset?.id);
                       }
                     });
                 }
             });
        } else {
            if (this.externalOverlaysFromGlobal) {

            } else {
                const dropDownData = JSON.parse(locationDataSetFieldsItem);
                // Create grouped structure for Locations
                this.fieldGroups = [{
                    groupName: 'Locations',
                    fields: dropDownData
                }];
                this.externalOverlays = dropDownData;

                // Fetch climate overlays for Locations scope
                // CLIMATE DISABLED: Commented out to hide climate from custom scoring
                // this.fetchClimateOverlays();

                // Fetch formulas for Locations scope
                const locationDataSetItem = localStorage.getItem("locationDataSetItem_"+this.accountId);
                if (locationDataSetItem) {
                    const locationDataset = JSON.parse(locationDataSetItem);
                    this.fetchFormulas(locationDataset?.id);
                }
            }
        }

    }

    // CLIMATE DISABLED: Commented out to hide climate from custom scoring
    // fetchClimateOverlays() {
    //     this.overlayService.getOverlayGroupsAsTree(this.accountId)
    //         .subscribe((res) => {
    //             if (res) {
    //                 const filteredTree = this.filterClimateGroups(res);
    //                 this.climateOverlays = filteredTree;
    //                 // Clear cache
    //                 this.uniqueGroupsCache = [];
    //                 this.overlaysByGroupCache = {};
    //                 // Add climate groups to fieldGroups with nested structure
    //                 // Handle parent Climate group with children
    //                 const processClimateGroups = (nodes: any[]) => {
    //                     nodes.forEach(node => {
    //                         // If this is the parent Climate group, add it as a group with children
    //                         if (node.group?.name === 'Climate' && node.children?.length > 0) {
    //                             // Add parent Climate group with all its children's overlays combined
    //                             node.children.forEach(child => {
    //                                 const overlays = child.overlays || [];
    //                                 overlays.forEach(overlay => {
    //                                     // Filter fields to only show displayInDropdowns: true
    //                                     const filteredFields = (overlay.fields || []).filter(field => field.displayInDropdowns === true);
    //                                     // Create composite keys for climate fields to avoid ID collisions
    //                                     const fieldsWithCompositeKeys = filteredFields.map(field => ({
    //                                         ...field,
    //                                         compositeId: `${overlay.id}_${field.id}`,
    //                                         originalId: field.id
    //                                     }));
    //                                     this.fieldGroups.push({
    //                                         groupName: 'Climate',
    //                                         subGroupName: child.group.name, // Add sub-group name for display
    //                                         overlayName: overlay.name,
    //                                         overlayId: overlay.id,
    //                                         datasetID: overlay.id,
    //                                         fields: fieldsWithCompositeKeys
    //                                     });
    //                                 });
    //                             });
    //                         } else {
    //                             // Process regular climate groups (if any direct climate groups exist)
    //                             const overlays = node.overlays || [];
    //                             overlays.forEach(overlay => {
    //                                 // Filter fields to only show displayInDropdowns: true
    //                                 const filteredFields = (overlay.fields || []).filter(field => field.displayInDropdowns === true);
    //                                 // Create composite keys for climate fields to avoid ID collisions
    //                                 const fieldsWithCompositeKeys = filteredFields.map(field => ({
    //                                     ...field,
    //                                     compositeId: `${overlay.id}_${field.id}`,
    //                                     originalId: field.id
    //                                 }));
    //                                 this.fieldGroups.push({
    //                                     groupName: node.group.name,
    //                                     overlayName: overlay.name,
    //                                     overlayId: overlay.id,
    //                                     datasetID: overlay.id,
    //                                     fields: fieldsWithCompositeKeys
    //                                 });
    //                             });
    //                         }
    //                     });
    //                 };
    //                 processClimateGroups(filteredTree);
    //             }
    //         });
    // }

    fetchFormulas(datasetId: string) {
        this.workspaceItemService.getAllWorkspaceItemsForUser(datasetId, WorkspaceItemType.FORMULA)
            .subscribe((formulas) => {
                this.formulas = (formulas || []).filter(formula => formula.access === WorkspaceItemAccess.GLOBAL);
                // Add formulas as a new group in fieldGroups (remove existing first to avoid duplicates)
                this.fieldGroups = this.fieldGroups.filter(g => g.groupName !== 'Formulas');
                if (this.formulas.length > 0) {
                    this.fieldGroups.push({
                        groupName: 'Formulas',
                        fields: this.formulas.map(formula => ({
                            id: formula.id,
                            name: formula.name,
                            displayName: formula.name,
                            type: 'FORMULA',
                            datasetId: formula.datasetId,
                            formulaData: formula.data
                        }))
                    });
                    // Clear caches so the template picks up the new group
                    this.uniqueGroupsCache = [];
                    this.overlaysByGroupCache = {};
                }
                this.changeDetector.detectChanges();
            });
    }

    // CLIMATE DISABLED: Commented out to hide climate from custom scoring
    // filterClimateGroups(nodes: any[]): any[] {
    //     if (!Array.isArray(nodes)) {
    //         return [];
    //     }
    //     const climateChildren: any[] = [];

    //     // Create a map of all nodes by their group ID for parent-child lookup
    //     const nodeMap = new Map();
    //     nodes.forEach(node => {
    //         if (node.group?.id) {
    //             nodeMap.set(node.group.id, node);
    //         }
    //     });

    //     for (const node of nodes) {
    //         const groupName = node.group?.name?.trim() || '';

    //         // Check if this is a climate group or a child of a climate group
    //         const isClimateGroup = this.climateGroupNames.includes(groupName);
    //         const isChildOfClimate = node.group?.parentID && nodeMap.has(node.group.parentID) &&
    //                                  this.climateGroupNames.includes(nodeMap.get(node.group.parentID).group?.name?.trim() || '');

    //         if (!isClimateGroup && !isChildOfClimate) {
    //             continue;
    //         }

    //         let filteredOverlays = [];
    //         if (Array.isArray(node.overlays)) {
    //             filteredOverlays = node.overlays.filter(ovl =>
    //               !(
    //                 ovl.isGeoserver === false ||
    //                 ovl.thematicMapSettings?.isThematicMapDataset === true ||
    //                 ovl.geometryType === 'POINT'
    //               )
    //             );
    //         }
    //         let filteredChildren = [];
    //         if (Array.isArray(node.children) && node.children.length > 0) {
    //             filteredChildren = this.filterClimateGroups(node.children);
    //         }
    //         const hasOverlays = (filteredOverlays.length > 0);
    //         const hasChildren = (filteredChildren.length > 0);
    //         if (hasOverlays || hasChildren) {
    //             climateChildren.push({
    //                 ...node,
    //                 overlays: filteredOverlays,
    //                 children: filteredChildren
    //             });
    //         }
    //     }

    //     // Create parent Climate group if there are climate children
    //     if (climateChildren.length > 0) {
    //         return [{
    //             group: { name: 'Climate' },
    //             overlays: [],
    //             children: climateChildren,
    //             isOpen: true
    //         }];
    //     }

    //     return [];
    // }
    onSelectionChange(selectedId: number) {
        const selectedData = this.externalOverlays.find(data => data.id === selectedId);
        this.selectedType = selectedData ? selectedData.type : '';
    }

    toggleGroupCollapse(groupName: string) {
        this.collapsedGroups[groupName] = !this.collapsedGroups[groupName];
    }

    isGroupCollapsed(groupName: string): boolean {
        return this.collapsedGroups[groupName] || false;
    }

    toggleOverlayCollapse(groupName: string, overlayName: string) {
        const key = `${groupName}_${overlayName}`;
        this.collapsedGroups[key] = !this.collapsedGroups[key];
    }

    isOverlayCollapsed(groupName: string, overlayName: string): boolean {
        const key = `${groupName}_${overlayName}`;
        return this.collapsedGroups[key] || false;
    }

    getUniqueGroups() {
        if (this.uniqueGroupsCache.length > 0) {
            return this.uniqueGroupsCache;
        }
        const uniqueGroups = [];
        const seenGroups = new Set();
        this.fieldGroups.forEach(group => {
            if (!seenGroups.has(group.groupName)) {
                seenGroups.add(group.groupName);
                uniqueGroups.push({ groupName: group.groupName });
            }
        });
        this.uniqueGroupsCache = uniqueGroups;
        return uniqueGroups;
    }

    getOverlaysForGroup(groupName: string) {
        if (this.overlaysByGroupCache[groupName]) {
            return this.overlaysByGroupCache[groupName];
        }
        const overlays = this.fieldGroups.filter(group => group.groupName === groupName);
        this.overlaysByGroupCache[groupName] = overlays;
        return overlays;
    }

    getSubGroupsForClimate() {
        const climateGroups = this.fieldGroups.filter(group => group.groupName === 'Climate');
        const uniqueSubGroups = [];
        const seenSubGroups = new Set();
        climateGroups.forEach(group => {
            if (group.subGroupName && !seenSubGroups.has(group.subGroupName)) {
                seenSubGroups.add(group.subGroupName);
                uniqueSubGroups.push({ subGroupName: group.subGroupName });
            }
        });
        return uniqueSubGroups;
    }

    getOverlaysForSubGroup(subGroupName: string) {
        return this.fieldGroups.filter(group => group.groupName === 'Climate' && group.subGroupName === subGroupName);
    }

    toggleSubGroupCollapse(subGroupName: string) {
        this.collapsedSubGroups[subGroupName] = !this.collapsedSubGroups[subGroupName];
    }

    isSubGroupCollapsed(subGroupName: string): boolean {
        return this.collapsedSubGroups[subGroupName] || false;
    }

    trackBySubGroupName(index: number, subGroup: any): string {
        return subGroup.subGroupName;
    }

    trackByGroupName(index: number, group: any): string {
        return group.groupName;
    }

    trackByOverlayName(index: number, overlay: any): string {
        return overlay.overlayName;
    }

    trackByFieldId(index: number, field: any): number {
        return field.id;
    }

    onSelectOpened(isOpened: boolean) {
        if (isOpened) {
            // Store the original value before opening the dropdown
            this.originalFieldValue = this.createAccountForm.get('fieldTextAndNumeric')?.value;
            
            if (!this.isDropdownInitialized) {
                // First time opening dropdown - collapse all groups by default
                this.initializeCollapsedState();
                this.isDropdownInitialized = true;
            }
        }
        
        if (!isOpened) {
            // When dropdown closes, check if a valid selection was made
            const currentValue = this.createAccountForm.get('fieldTextAndNumeric')?.value;
            
            // If the current value is null, undefined, empty, or a group header, restore the original value
            if (currentValue === null || currentValue === undefined || currentValue === '' || 
                (currentValue && currentValue.toString().startsWith('group_header_'))) {
                // Restore the original value if it existed
                if (this.originalFieldValue !== null && this.originalFieldValue !== undefined && this.originalFieldValue !== '') {
                    this.createAccountForm.get('fieldTextAndNumeric').setValue(this.originalFieldValue, { emitEvent: false });
                }
            }
            // Clear the stored original value
            this.originalFieldValue = null;
        }
    }
    
    initializeCollapsedState(): void {
        // Collapse all top-level groups by default (only if not already set)
        const uniqueGroups = this.getUniqueGroups();
        uniqueGroups.forEach(group => {
            if (this.collapsedGroups[group.groupName] === undefined) {
                // Collapse all groups by default
                this.collapsedGroups[group.groupName] = true;
            }
        });
        
        // Collapse all Climate sub-groups (only if not already set)
        const climateSubGroups = this.getSubGroupsForClimate();
        climateSubGroups.forEach(subGroup => {
            if (this.collapsedSubGroups[subGroup.subGroupName] === undefined) {
                this.collapsedSubGroups[subGroup.subGroupName] = true;
            }
        });
        
        // Collapse all overlays within groups (only if not already set)
        this.fieldGroups.forEach(group => {
            if (group.overlayName) {
                const key = `${group.groupName}_${group.overlayName}`;
                if (this.collapsedGroups[key] === undefined) {
                    this.collapsedGroups[key] = true;
                }
            }
        });
    }

    onFieldSelected(fieldData: any, datasetID: string) {
        this.selectedGroupDatasetID = datasetID;
        // If a climate datasetID is set, update the scope to GLOBAL
        if (datasetID) {
            this.customScoringScope = CustomScoringScope.GLOBAL;
        } else if (fieldData.type === 'FORMULA') {
            // If formula field is selected, set scope to FORMULA
            this.customScoringScope = CustomScoringScope.FORMULA;
            this.selectedGroupDatasetID = fieldData.datasetId || '';
        } else {
            // Clear climate scope when location field is selected
            this.customScoringScope = CustomScoringScope.INTERNAL;
        }
    }
    get ranges() {
        return this.createAccountForm.get('ranges') as FormArray;
    }
    
    onFieldTypeChange(event: any, isComingFromExisting = false) {
        // Early return if no valid value is selected (e.g., clicking outside dropdown or on headers)
        if (event.value === null || event.value === undefined || event.value === '') {
            return;
        }
        // If a group header was accidentally selected, revert to the previous value
        if (event.value && event.value.toString().startsWith('group_header_')) {
            if (this.originalFieldValue !== null && this.originalFieldValue !== undefined && this.originalFieldValue !== '') {
                this.createAccountForm.get('fieldTextAndNumeric').setValue(this.originalFieldValue, { emitEvent: false });
            } else {
                this.createAccountForm.get('fieldTextAndNumeric').setValue('', { emitEvent: false });
            }
            return;
        }
        
        let isUpdateValueChanged = false;
        let selectedData;
        let isClimateField = false;
        let isFormulaField = false;
        if (this.existingData && (this.customScoringScope === CustomScoringScope.INTERNAL || this.customScoringScope === 'Locations')) {
            isUpdateValueChanged = String(event.value) !== String(this.existingData?.fieldId) && event.value.length <= 1;
        } else if (this.existingData && this.customScoringScope === CustomScoringScope.FORMULA) {
            // For FORMULA scope, compare with fieldId since patchExistingData uses fieldId
            // Convert both to strings to handle number vs string comparison
            isUpdateValueChanged = String(event.value) !== String(this.existingData?.fieldId);
        } else if (this.existingData && this.customScoringScope === CustomScoringScope.GLOBAL) {
            // For GLOBAL scope (climate), compare with composite ID since patchExistingData uses ${datasetID}_${fieldId}
            const compositeId = `${this.existingData.datasetID}_${this.existingData.fieldId}`;
            isUpdateValueChanged = String(event.value) !== String(compositeId);
        }

        if (this.existingData && !isUpdateValueChanged) {
            // For FORMULA scope, search in fieldGroups
            if (this.customScoringScope === CustomScoringScope.FORMULA) {
                if (this.fieldGroups && this.fieldGroups.length > 0) {
                    for (const group of this.fieldGroups) {
                        if (group.groupName === 'Formulas') {
                            // Search by both id and name for formulas
                            const found = group.fields?.find(data => data && (data.id === this.existingData.fieldId || data.name === this.existingData.fieldName));
                            if (found) {
                                selectedData = found;
                                isFormulaField = true;
                                isClimateField = false;  // Explicitly set to false for formulas
                                this.selectedGroupDatasetID = selectedData.datasetId || '';
                                break;
                            }
                        }
                    }
                }
            } else if (this.customScoringScope === CustomScoringScope.GLOBAL) {
                // For GLOBAL scope (climate fields), search in fieldGroups with composite ID
                if (this.fieldGroups && this.fieldGroups.length > 0) {
                    for (const group of this.fieldGroups) {
                        const found = group.fields?.find(data => data && data.originalId === this.existingData.fieldId);
                        if (found) {
                            selectedData = found;
                            isClimateField = true;
                            this.selectedGroupDatasetID = group.datasetID || this.existingData.datasetID;
                            break;
                        }
                    }
                }
                // If not found in fieldGroups, try externalOverlaysFromGlobal
                if (!selectedData && this.externalOverlaysFromGlobal) {
                    const found = this.externalOverlaysFromGlobal.find(data => data && data.id === this.existingData.fieldId);
                    if (found) {
                        selectedData = found;
                        isClimateField = true;
                        this.selectedGroupDatasetID = this.existingData.datasetID;
                    }
                }
            } else {
                // For EXTERNAL/INTERNAL scope, search in externalOverlays or fieldGroups
                if (this.customScoringScope === CustomScoringScope.INTERNAL || this.customScoringScope === 'Locations') {
                    // For INTERNAL scope, search by fieldId in fieldGroups first
                    if (this.fieldGroups && this.fieldGroups.length > 0) {
                        for (const group of this.fieldGroups) {
                            if (group.groupName === 'Locations') {
                                const found = group.fields?.find(data => data && data.id === this.existingData.fieldId);
                                if (found) {
                                    selectedData = found;
                                    isFormulaField = false;  // Explicitly set to false for locations
                                    isClimateField = false;  // Explicitly set to false for locations
                                    break;
                                }
                            }
                        }
                    }
                    // Fallback to externalOverlays if not found
                    if (!selectedData) {
                        selectedData = this.externalOverlays.find(data => data.id === this.existingData.fieldId);
                        if (selectedData) {
                            isFormulaField = false;
                            isClimateField = false;
                        }
                    }
                }
            }
            if (selectedData) {
                this.createAccountForm.controls.fieldTextAndNumeric.setValue(selectedData.compositeId || selectedData.id);
            }

        } else {
            // Check if this is a composite ID (climate field)
            const isCompositeId = event.value && typeof event.value === 'string' && event.value.includes('_');
            if (isCompositeId) {
                // Parse composite ID: datasetID_fieldId (split on last underscore since dataset IDs may contain underscores)
                const lastUnderscoreIndex = event.value.lastIndexOf('_');
                const overlayId = event.value.substring(0, lastUnderscoreIndex);
                const fieldId = event.value.substring(lastUnderscoreIndex + 1);
                // Search in fieldGroups for the composite ID
                if (this.fieldGroups && this.fieldGroups.length > 0) {
                    for (const group of this.fieldGroups) {
                        if (group.fields && group.fields.length > 0) {
                            const found = group.fields.find(data => data && data.compositeId === event.value);
                            if (found) {
                                selectedData = found;
                                isClimateField = true;
                                // Set the datasetID from the group
                                this.selectedGroupDatasetID = group.datasetID || '';
                                // Set scope to GLOBAL for climate fields
                                this.customScoringScope = CustomScoringScope.GLOBAL;
                                break;
                            }
                        }
                    }
                }
                // If not found in fieldGroups, try externalOverlaysFromGlobal (for editing)
                if (!selectedData && this.externalOverlaysFromGlobal) {
                    const found = this.externalOverlaysFromGlobal.find(data => data && data.id === fieldId);
                    if (found) {
                        selectedData = found;
                        isClimateField = true;
                        this.selectedGroupDatasetID = this.selectedGroupDatasetID || overlayId;
                        // Set scope to GLOBAL for climate fields
                        this.customScoringScope = CustomScoringScope.GLOBAL;
                    }
                }
            } else {
                // Regular ID (location field or formula) - search in fieldGroups first
                if (this.fieldGroups && this.fieldGroups.length > 0) {
                    for (const group of this.fieldGroups) {
                        const found = group.fields?.find(data => data && data.id === event.value);
                        if (found) {
                            selectedData = found;
                            isFormulaField = group.groupName === 'Formulas';
                            // isClimateField should be true only for climate groups
                            // Climate groups have groupName: 'Climate', 'Climate Change', or 'Climate Change Risk Score'
                            isClimateField = group.groupName === 'Climate' || this.climateGroupNames.includes(group.groupName);
                            // Set the datasetID from the group
                            this.selectedGroupDatasetID = group.datasetID || '';
                            // If formula, set scope to FORMULA
                            if (isFormulaField) {
                                this.customScoringScope = CustomScoringScope.FORMULA;
                                this.selectedGroupDatasetID = selectedData.datasetId || '';
                            } else if (isClimateField) {
                                // If climate field, set scope to GLOBAL
                                this.customScoringScope = CustomScoringScope.GLOBAL;
                            }
                            break;
                        }
                    }
                }
                // If not found in fieldGroups, try externalOverlays (for backward compatibility)
                if (!selectedData) {
                    selectedData = this.externalOverlays.find(data => data && data.id === event.value);
                    if (selectedData) {
                        // Found in externalOverlays, so it's a location field
                        this.selectedGroupDatasetID = '';
                        isClimateField = false;
                        isFormulaField = false;
                    }
                }
            }
        }
        const selectedFieldType = selectedData ? selectedData.type : '';
        this.isNumberField = selectedFieldType === 'NUMBER_FIELD' || selectedFieldType === 'FORMULA';
        this.isTextField = selectedFieldType === 'SHORT_TEXT_FIELD';
        this.isDropdownValueSelected = true;
        this.ranges.clear();
        this.ranges.clearValidators();
        if (this.isNumberField || this.isTextField) {
            this.ranges.setValidators([Validators.required]);
        }
        this.ranges.updateValueAndValidity();
        if (!this.isEdit || isUpdateValueChanged) {
            this.addRange();
        }
        if (this.locationDataset === undefined || this.locationDataset === null) {
            this.locationDataset = JSON.parse(localStorage.getItem("locationDataSetItem_"+this.accountId));
        }
        
        // Always fetch statistics for proper display of min/max values and decimal detection
        // Check field type and route to appropriate statistics API
        if (selectedData && isFormulaField) {
            // For formulas, always use location dataset with all groups
            // Formulas are calculated fields that use location data
            this.groupService.getGroupsAsTreeForCustomScoring(this.locationDataset.id, this.accountId).subscribe(groups => {
                const ids: number[] = [];
                const collectIds = (nodes: any[]) => {
                  nodes.forEach(node => {
                    ids.push(node.value.id);
                    if (node.children && node.children.length > 0) {
                      collectIds(node.children);
                    }
                  });
                };
                collectIds(groups);
                this.datapointsAggregateService.getDatapointsFieldStatistics(this.locationDataset.id, selectedData.id, {
                    datasetID: this.locationDataset.id,
                    groups: ids
                })
                .subscribe((statistics) => {
                    this.textOptions = statistics?.values || [];
                    this.minMaxValues = {'minValue' : 'Statistical Minimum: '+(statistics?.minValue !== null ? statistics?.minValue.toLocaleString() : statistics?.minValue), 'maxValue':'Statistical Maximum: '+ (statistics?.maxValue !== null ? statistics?.maxValue.toLocaleString() : statistics?.maxValue)};
                    this.minValues = statistics?.minValue;

                    // Detect if field has decimal values
                    if (statistics?.minValue !== undefined && statistics?.maxValue !== undefined) {
                        this.fieldHasDecimals = this.checkIfFieldHasDecimals(statistics.minValue, statistics.maxValue);
                    }

                    this.changeDetector.detectChanges();
                });
            });
        } else if (this.selectedGroupDatasetID && isClimateField && selectedData) {
            // For climate fields, use the climate datasetID and empty groups
            // Use original field ID (not composite ID) for the API call
            const fieldIdForApi = selectedData.originalId || selectedData.id;
            this.datapointsAggregateService.getDatapointsFieldStatistics(this.selectedGroupDatasetID, fieldIdForApi, {
                datasetID: this.selectedGroupDatasetID,
                groups: []
            })
            .subscribe((statistics) => {
                this.textOptions = statistics?.values || [];
                this.minMaxValues = {'minValue' : 'Statistical Minimum: '+(statistics?.minValue !== null ? statistics?.minValue.toLocaleString() : statistics?.minValue), 'maxValue':'Statistical Maximum: '+ (statistics?.maxValue !== null ? statistics?.maxValue.toLocaleString() : statistics?.maxValue)};
                this.minValues = statistics?.minValue;

                // Detect if field has decimal values
                if (statistics?.minValue !== undefined && statistics?.maxValue !== undefined) {
                    this.fieldHasDecimals = this.checkIfFieldHasDecimals(statistics.minValue, statistics.maxValue);
                }

                // Min value is no longer auto-filled - user must define it explicitly
                this.changeDetector.detectChanges();
            });
        } else if (selectedData && !isClimateField) {
            // For location fields (both use location dataset for statistics)
            this.groupService.getGroupsAsTreeForCustomScoring(this.locationDataset.id, this.accountId).subscribe(groups => {
                const ids: number[] = [];
                const collectIds = (nodes: any[]) => {
                  nodes.forEach(node => {
                    ids.push(node.value.id);
                    if (node.children && node.children.length > 0) {
                      collectIds(node.children);
                    }
                  });
                };
                collectIds(groups);
                const fieldId = selectedData.id;
                this.datapointsAggregateService.getDatapointsFieldStatistics(this.locationDataset.id, fieldId, {

                    datasetID: this.locationDataset.id,
                    groups: ids
                })
                .subscribe((statistics) => {
                    this.textOptions = statistics?.values || [];
                    this.minMaxValues = {'minValue' : 'Statistical Minimum: '+(statistics?.minValue !== null ? statistics?.minValue.toLocaleString() : statistics?.minValue), 'maxValue':'Statistical Maximum: '+ (statistics?.maxValue !== null ? statistics?.maxValue.toLocaleString() : statistics?.maxValue)};
                    this.minValues = statistics?.minValue;

                    // Detect if field has decimal values
                    if (statistics?.minValue !== undefined && statistics?.maxValue !== undefined) {
                        this.fieldHasDecimals = this.checkIfFieldHasDecimals(statistics.minValue, statistics.maxValue);
                    }

                    // Min value is no longer auto-filled - user must define it explicitly
                    this.changeDetector.detectChanges();
                });
            });
        }
    }

    addRange() {
        let prePopulatedMin = '';
        let isPrePopulated = false;
        
        // If there are existing ranges and this is a numeric field, get the max value of the last range
        if (this.ranges.length > 0 && this.isNumberField) {
            const lastRange = this.ranges.at(this.ranges.length - 1);
            const lastMaxValue = lastRange.get('maxValue')?.value;
            
            if (lastMaxValue && lastMaxValue !== '') {
                // Clean the value (remove commas) and format to 2 decimals
                const cleanValue = lastMaxValue.toString().replace(/,/g, '');
                const numValue = parseFloat(cleanValue);
                
                // Validate number is safe
                if (!isNaN(numValue) && isFinite(numValue) && Math.abs(numValue) <= Number.MAX_SAFE_INTEGER) {
                    // Always preserve decimal values, limit to 2 decimals if more exist
                    const rounded = Math.round(numValue * 100) / 100;
                    
                    // Format with commas without rounding (preserve exact decimal value)
                    let formattedValue: string;
                    if (rounded % 1 !== 0) {
                        // Has decimals - preserve trailing zeros with toFixed(2)
                        formattedValue = rounded.toFixed(2);
                    } else {
                        // Whole number
                        formattedValue = rounded.toString();
                    }
                    
                    // Add commas manually
                    const parts = formattedValue.split('.');
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    prePopulatedMin = parts.join('.');
                    isPrePopulated = true;
                }
            }
        }
        
        const rangeGroup = this.fb.group({
            minValue: [prePopulatedMin],  // Pre-populate but keep editable
            maxValue: [''],  // No required validator - allows open-ended ranges
            textValues: [
                this.isTextField ? [] : null,
                this.isTextField ? Validators.required : [],
            ],
            label: ['', Validators.required],
            isMinPrePopulated: [isPrePopulated],  // Track if min was pre-populated
            originalPrePopulatedMin: [prePopulatedMin]  // Store original value for validation
        });
    
        this.ranges.push(rangeGroup);
      
    }
    removeRange(index: number) {
        this.ranges.removeAt(index);
        
        // After removing a range, update the next range's min value if it was pre-populated
        if (index < this.ranges.length && this.isNumberField) {
            this.updateNextRangeMinValue(index);
        }
    }
    
    validateMinValue(index: number): void {
        if (!this.isNumberField) {
            return;
        }
        
        const currentRange = this.ranges.at(index);
        const isPrePopulated = currentRange.get('isMinPrePopulated')?.value;
        
        if (isPrePopulated && index > 0) {
            const previousRange = this.ranges.at(index - 1);
            const previousMaxStr = previousRange.get('maxValue')?.value?.toString().replace(/,/g, '') || '0';
            const currentMinStr = currentRange.get('minValue')?.value?.toString().replace(/,/g, '') || '0';
            
            const previousMax = parseFloat(previousMaxStr);
            const currentMin = parseFloat(currentMinStr);
            
            // Validate numbers are safe before comparison
            if (isNaN(previousMax) || isNaN(currentMin) || !isFinite(previousMax) || !isFinite(currentMin)) {
                return; // Skip validation if numbers are invalid
            }
            
            if (currentMin < previousMax) {
                // Reset to previous max value, always preserve decimals (limit to 2 decimals)
                const rounded = Math.round(previousMax * 100) / 100;
                // Check if the number has decimal places
                let formattedValue: string;
                if (rounded % 1 !== 0) {
                    // Has decimals - preserve trailing zeros with toFixed(2)
                    formattedValue = rounded.toFixed(2);
                } else {
                    // Whole number - don't add unnecessary .00
                    formattedValue = rounded.toString();
                }
                currentRange.get('minValue')?.setValue(formattedValue);
                this.notifService.error('Min value cannot be less than the previous range\'s max value.');
            }
        }
    }
    
    onMaxValueChange(index: number): void {
        if (!this.isNumberField) {
            return;
        }
        
        // When max value changes, update the next range's min value if it exists
        if (index < this.ranges.length - 1) {
            this.updateNextRangeMinValue(index);
        }
    }
    
    updateNextRangeMinValue(currentIndex: number): void {
        const currentRange = this.ranges.at(currentIndex);
        const nextRange = this.ranges.at(currentIndex + 1);
        
        if (!nextRange) {
            return;
        }
        
        const isNextMinPrePopulated = nextRange.get('isMinPrePopulated')?.value;
        
        // Only update if the next range's min was pre-populated
        if (isNextMinPrePopulated) {
            const currentMaxValue = currentRange.get('maxValue')?.value;
            
            if (currentMaxValue && currentMaxValue !== '') {
                const cleanValue = currentMaxValue.toString().replace(/,/g, '');
                const numValue = parseFloat(cleanValue);
                
                // Validate number is safe
                if (!isNaN(numValue) && isFinite(numValue) && Math.abs(numValue) <= Number.MAX_SAFE_INTEGER) {
                    // Always preserve decimal values, limit to 2 decimals if needed
                    const rounded = Math.round(numValue * 100) / 100;
                    
                    // Format with commas without rounding (preserve exact decimal value)
                    let formattedValue: string;
                    if (rounded % 1 !== 0) {
                        // Has decimals - preserve trailing zeros with toFixed(2)
                        formattedValue = rounded.toFixed(2);
                    } else {
                        // Whole number
                        formattedValue = rounded.toString();
                    }
                    
                    // Add commas manually
                    const parts = formattedValue.split('.');
                    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                    const formatted = parts.join('.');
                    
                    // Update the value and the original pre-populated value
                    nextRange.get('minValue')?.setValue(formatted, { emitEvent: false });
                    nextRange.get('originalPrePopulatedMin')?.setValue(formatted, { emitEvent: false });
                }
            }
        }
    }

    drop(event: CdkDragDrop<FormGroup[]>) {
        moveItemInArray(this.ranges.controls, event.previousIndex, event.currentIndex);
        this.updatePayloadOrder();
    }
    
      updatePayloadOrder() {
        this.ranges.controls.forEach((control, index) => {
          control.patchValue({ order: index + 1 });
        });
    }

    moveRangeUp(index: number) {
        if (index === 0) return;
        const ranges = this.ranges.controls;
        [ranges[index - 1], ranges[index]] = [ranges[index], ranges[index - 1]];
        this.updatePayloadOrder();
    }
    
      moveRangeDown(index: number) {
        if (index === this.ranges.length - 1) return;
        const ranges = this.ranges.controls;
        [ranges[index], ranges[index + 1]] = [ranges[index + 1], ranges[index]];
        this.updatePayloadOrder();
    }

    get hasValidationError() {
        // This validation only applies to numeric fields
        if (!this.isNumberField) {
            return false;
        }
        
        return this.ranges.controls.some((control, index) => {
          const minVal = control.get('minValue')?.value?.toString().replace(/,/g, '') || '';
          const maxVal = control.get('maxValue')?.value?.toString().replace(/,/g, '') || '';
          const isFirstRange = index === 0;
          const isLastRange = index === this.ranges.controls.length - 1;
          
          // Check for invalid characters (spaces or letters)
          if (maxVal && (/\s/.test(maxVal) || /[a-zA-Z]/.test(maxVal))) {
            return true;
          }
          if (minVal && (/\s/.test(minVal) || /[a-zA-Z]/.test(minVal))) {
            return true;
          }
          
          // Check if values are valid numbers when provided
          if (minVal !== '' && isNaN(+minVal)) {
            return true;
          }
          if (maxVal !== '' && isNaN(+maxVal)) {
            return true;
          }
          
          // First range: min can be blank (open-ended: all values <= max)
          // Last range: max can be blank (open-ended: all values >= min)
          // Middle ranges: both min and max are required
          if (!isFirstRange && !isLastRange) {
            // Middle range - both values required
            if (minVal === '' || maxVal === '') {
              return true;
            }
          } else if (isFirstRange && this.ranges.controls.length > 1) {
            // First range with multiple ranges - max is required
            if (maxVal === '') {
              return true;
            }
          } else if (isLastRange && this.ranges.controls.length > 1) {
            // Last range with multiple ranges - min is required
            if (minVal === '') {
              return true;
            }
          }
          
          // If both values provided, min should not be greater than max
          if (minVal !== '' && maxVal !== '') {
            return +minVal > +maxVal;
          }
          
          return false;
        });
    }
    
    get hasOverlappingRanges(): boolean {
        if (!this.isNumberField || this.ranges.controls.length < 2) {
            return false;
        }
        
        // Parse ranges into numeric intervals
        const intervals = this.ranges.controls.map((control, index) => {
            const minVal = control.get('minValue')?.value?.toString().replace(/,/g, '') || '';
            const maxVal = control.get('maxValue')?.value?.toString().replace(/,/g, '') || '';
            
            return {
                index,
                min: minVal === '' ? -Infinity : +minVal,
                max: maxVal === '' ? Infinity : +maxVal
            };
        });
        
        // Sort intervals by min value
        const sortedIntervals = [...intervals].sort((a, b) => a.min - b.min);
        
        // Check for overlaps and minimum gap between consecutive sorted intervals
        for (let i = 0; i < sortedIntervals.length - 1; i++) {
            const current = sortedIntervals[i];
            const next = sortedIntervals[i + 1];
            
            // Calculate the gap between current max and next min
            const gap = next.min - current.max;
            
            // Overlap exists only if current max > next min (true overlap)
            // Adjacent ranges (e.g., <20000 and >=20000) are allowed since max is exclusive
            if (current.max > next.min) {
                return true;
            }
        }
        
        return false;
    }
    
    initForm(): void {
        this.isFormDisabled = false;
        this.createAccountForm = this.fb.group({
            name: ['', [Validators.required]],
            fieldTextAndNumeric: ['', [Validators.required]],
            ranges: this.fb.array([]),

        });
        this.createAccountForm.get('ranges').clearValidators();  // CLEAR ALL VALIDATORS INITIALLY
        this.createAccountForm.updateValueAndValidity();
        // Reset climate-related state
        this.selectedGroupDatasetID = '';
        if (this.customScoringScope === CustomScoringScope.GLOBAL && !this.externalOverlaysFromGlobal) {
            // Reset to INTERNAL if we're not editing a climate custom scoring
            this.customScoringScope = CustomScoringScope.INTERNAL;
        }
    }

    mapItems(items) {
        let values = [];
        Object.keys(items).map((index) => {
            let item = items[index];
            if (item) {
                values.push(index);
            }
        });
        return !(values.length === 0);
    }
    clearFormValidators(formGroup: UntypedFormGroup | FormGroup): void {
        Object.keys(formGroup.controls).forEach((key) => {
          const control = formGroup.get(key);
          if (control instanceof UntypedFormGroup || control instanceof FormGroup) {
            this.clearFormValidators(control);
          } else if (control instanceof UntypedFormControl) {
            control.clearValidators();
            control.updateValueAndValidity();
          } else {
            control.clearValidators();
            control.updateValueAndValidity();
          }
        });
      }

      
    onCancel() {
        this.onCancelClose.emit();
        this.changeDetector.detectChanges();
    }
    submitAccount(): void {
        this.createAccountForm.markAllAsTouched();
        if (this.createAccountForm.valid && !this.hasValidationError && !this.hasOverlappingRanges) {

            const formData = this.createAccountForm.value;
        let hasErrors = false;
        formData.name = formData.name?.trim();
        if (!formData.name) {
            this.createAccountForm.controls['name'].setErrors({ required: true });
            this.createAccountForm.controls['name'].markAsTouched();
            hasErrors = true;
        }
        let tempDataSetId;
        if (this.customScoringScope === CustomScoringScope.EXTERNAL) {
            tempDataSetId = this.globalDataSetRecord.datasetId;
        } else {
            tempDataSetId = this.locationDataset.id;
        }
        let datasetID = this.globalDataSetRecord ? this.globalDataSetRecord.id : '';
        let scope = this.customScoringScope;
        if (this.customScoringScope === CustomScoringScope.INTERNAL || this.customScoringScope === 'Locations') {
            // Check if selected field is from climate group
            if (this.selectedGroupDatasetID) {
                datasetID = this.selectedGroupDatasetID;
                scope = 'GLOBAL';
            } else {
                datasetID = 'Locations'
                scope = 'INTERNAL';
            }
        } else if (this.customScoringScope === CustomScoringScope.GLOBAL && this.selectedGroupDatasetID) {
            // For GLOBAL scope with climate fields, use selectedGroupDatasetID
            datasetID = this.selectedGroupDatasetID;
        } else if (this.customScoringScope === CustomScoringScope.FORMULA) {
            // For FORMULA scope, use the formula's datasetId
            datasetID = this.selectedGroupDatasetID;
            scope = 'FORMULA';
        }
        // Find selected data - handle both composite IDs (climate), regular IDs (location), and formulas
        let selectedData;
        const isCompositeId = formData.fieldTextAndNumeric && typeof formData.fieldTextAndNumeric === 'string' && formData.fieldTextAndNumeric.includes('_');
        if (isCompositeId) {
            // Climate field - search in fieldGroups
            for (const group of this.fieldGroups) {
                const found = group.fields?.find(data => data.compositeId === formData.fieldTextAndNumeric);
                if (found) {
                    selectedData = found;
                    break;
                }
            }
        } else {
            // Location field or formula - search in fieldGroups first
            for (const group of this.fieldGroups) {
                const found = group.fields?.find(data => data.id === formData.fieldTextAndNumeric);
                if (found) {
                    selectedData = found;
                    break;
                }
            }
            // If not found in fieldGroups, try externalOverlays (for backward compatibility)
            if (!selectedData) {
                selectedData = this.externalOverlays.find(data => data.id === formData.fieldTextAndNumeric);
            }
        }
        // Use original field ID for climate fields, formula ID for formulas
        const fieldIdForPayload = selectedData?.originalId || selectedData?.id;
            const payload = {
                name: formData.name,
                accountId: this.accountId,
                deleted: false,
                scope: scope,
                datasetID: datasetID,
                fieldName: selectedData?.name,
                fieldId: String(fieldIdForPayload),
                fieldType: selectedData?.type,
                fieldTextAndNumeric: String(formData.fieldTextAndNumeric),
                isNew: true,
                scoring: formData.ranges.map((range, index) => {
                    const trimmedLabel = range.label?.trim();                    
                    if (!trimmedLabel) {
                        const control = ((this.createAccountForm.get('ranges') as FormArray).at(index) as FormGroup).get('label');
                        control.setErrors({ required: true });
                        control.markAsTouched();
                        hasErrors = true;
                    }
                    
                    // Get the actual value including disabled controls
                    const rangeControl = this.ranges.at(index);
                    const minValueRaw = rangeControl.get('minValue')?.value || range.minValue || '';
                    const maxValueRaw = rangeControl.get('maxValue')?.value || range.maxValue || '';
                    
                    // Clean the values (remove commas) and convert to numbers
                    const minCleaned = minValueRaw ? minValueRaw.toString().replace(/,/g, '') : '';
                    const maxCleaned = maxValueRaw ? maxValueRaw.toString().replace(/,/g, '') : '';
                    const minNumberValue = minCleaned ? parseFloat(minCleaned) : null;
                    const maxNumberValue = maxCleaned ? parseFloat(maxCleaned) : null;

                    return {
                        minNumberValue: minNumberValue,
                        maxNumberValue: maxNumberValue,
                        textValues: range.textValues,
                        label: trimmedLabel,
                        order: index + 1,
                    };
                })
            };
            if (hasErrors) {
                return;
            }
            if (this.existingData && this.existingData.length !== 0) {
                // Use original field ID for climate fields, regular ID for others
                const fieldIdForUpdate = selectedData?.originalId || selectedData?.id;
                
                const payload = {
                    _id: this.existingData._id,
                    name: formData.name,
                    accountId: this.accountId,
                    deleted: false,
                    scope: this.existingData.scope,
                    datasetID: this.existingData.datasetID,
                    fieldName: selectedData.name,
                    fieldId: String(fieldIdForUpdate),
                    fieldType: selectedData.type,
                    fieldTextAndNumeric: String(formData.fieldTextAndNumeric),
                    isNew: true,
                    scoring: formData.ranges.map((range, index) => {
                        // Get the actual value including disabled controls
                        const rangeControl = this.ranges.at(index);
                        const minValueRaw = rangeControl.get('minValue')?.value || range.minValue || '';
                        const maxValueRaw = rangeControl.get('maxValue')?.value || range.maxValue || '';
                        
                        // Clean the values (remove commas) and convert to numbers
                        const minCleaned = minValueRaw ? minValueRaw.toString().replace(/,/g, '') : '';
                        const maxCleaned = maxValueRaw ? maxValueRaw.toString().replace(/,/g, '') : '';
                        const minNumberValue = minCleaned ? parseFloat(minCleaned) : null;
                        const maxNumberValue = maxCleaned ? parseFloat(maxCleaned) : null;

                        return {
                            minNumberValue: minNumberValue,
                            maxNumberValue: maxNumberValue,
                            textValues: range.textValues,
                            label: range.label,
                            order: index + 1,
                        };
                    })
                };
                this.customService.updateCustomScoring(this.accountId, payload)
                .pipe(
                    catchError((error) => {
                        console.error("Update Custom Scoring Error: ", error);
                        if (error?.error?.errors) {
                            this.notifService.error(error.error.errors);
                        } else if (error?.error?.message) {
                            this.notifService.error(error.error.message);
                        } else if (error?.message) {
                            this.notifService.error(error.message);
                        } else {
                            this.notifService.error('Failed to update custom scoring. Please try again.');
                        }
                        return of(null);
                    })
                )
                .subscribe((data) => {
                    if (data !== null) {
                        if (this.isEdit) {
                            this.notifService.success(
                                "Successfully updated custom scoring"
                            );
                        } else {
                            this.notifService.success(
                                "Successfully created custom scoring"
                           );
                        }
                        this.updatedSuccessfully.emit();
                        this.changeDetector.detectChanges();
                    }
                });
            } else {

                try {
                let apiError: any;
                this.customService.createCustomScoring(this.accountId, payload)
                .pipe(
                    catchError((error) => {
                        console.error("API Error: ", error);
                        apiError = error;
                        return of(null);
                    })
                )

                .subscribe((data) => {
                   if (apiError?.error?.errors) {
                     this.notifService.error(apiError.error.errors);
                   } else if (apiError?.error?.message) {
                     this.notifService.error(apiError.error.message);
                   } else if (apiError?.message) {
                     this.notifService.error(apiError.message);
                   } else if (apiError) {
                     this.notifService.error('Failed to save custom scoring. Server returned: ' + apiError.status + ' ' + apiError.statusText);
                   } else {
                    this.existingData = data;
                    this.isEdit = true;
                    this.notifService.success(
                        "Successfully created custom scoring"
                    );
                    this.updatedSuccessfully.emit();
                    this.changeDetector.detectChanges();
                   }

                });
            } catch (error) {
                console.error("Unexpected Error: ", error);
            }
        } 

          } else {
          }
    }

    getSelectedDatasets(): string[] {
        let checkboxes = this.createAccountForm.controls.checkboxes.value;
        let datasetNames = [];
        for (const dataset in checkboxes) {
            if (checkboxes.hasOwnProperty(dataset) && checkboxes[dataset]) {
                datasetNames.push(dataset.toUpperCase());
            }
        }
        return datasetNames;
    }

    removeSpaces(index = null) {
        const nameControl = this.createAccountForm.get('name');
        const labelControl = index !== null ? (this.createAccountForm.get('ranges') as FormArray).at(index).get('label') : null;
        [nameControl, labelControl].forEach(control => this.handleFormSpaceRemoval(control));
    }

    handleFormSpaceRemoval(controlName) {
        if (controlName && controlName.value) {
            controlName.setValue(controlName.value.replace(/^\s+/, ''), { emitEvent: false });
        }
    }

    getSelectedTextValues(currentIndex: number) {
        const selectedValues = [];
        this.ranges.controls.forEach((control, index) => {
            if (index !== currentIndex) {
                const textValues = control.get('textValues')?.value;
                if (textValues && Array.isArray(textValues)) {
                    selectedValues.push(...textValues);
                }
            }
        });
        return selectedValues;
    }

    isOptionDisabled(option: string, currentIndex: number) {
        return this.getSelectedTextValues(currentIndex).includes(option);
    }
    
    getAllowMinEdit(index: number) {
        const range = this.ranges.at(index);
        return !range?.get('isMinPrePopulated')?.value;
    }

    /**
     * Decode HTML-encoded text for display
     * @param text - The text to decode
     * @returns Decoded text
     */
    getDecodedText(text: string): string {
        if (!text) return '';
        return decodeHtmlText(text);
    }

    /**
     * Get the display name for a field, decoding HTML entities
     * @param data - The field data object
     * @returns Decoded display name or name
     */
    getFieldDisplayName(data: any): string {
        const name = data?.displayName || data?.name || '';
        return this.getDecodedText(name);
    }

}
